import React, { useEffect, useRef, useState } from "react";
import "./BookSettings.scss";
import { API_BASE_URL } from "../../config";
import { authHeaders } from "../../utils/api";

const CATEGORIES = [
    { value: '',          label: 'بدون تصنيف' },
    { value: 'novel',     label: 'رواية' },
    { value: 'short',     label: 'قصة قصيرة' },
    { value: 'poetry',    label: 'شعر' },
    { value: 'play',      label: 'مسرحية' },
    { value: 'essay',     label: 'مقالات' },
    { value: 'memoir',    label: 'سيرة ذاتية' },
    { value: 'scifi',     label: 'خيال علمي' },
    { value: 'mystery',   label: 'غموض' },
    { value: 'romance',   label: 'رومانسية' },
    { value: 'historic',  label: 'تاريخي' },
    { value: 'children',  label: 'أطفال' },
    { value: 'other',     label: 'أخرى' },
];

function absoluteUrl(path) {
    if (!path) return '';
    if (/^https?:/i.test(path)) return path;
    return `${API_BASE_URL}${path.startsWith('/') ? '' : '/'}${path}`;
}

export default function BookSettings({
    open,
    onClose,
    bookId,
    authTokens,
    settings,
    onChange,
    onCoverChange,
    onUploadError,
}) {
    const fileRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [dimensions, setDimensions] = useState(null);

    useEffect(() => {
        if (!settings.coverImage) { setDimensions(null); return; }
        const img = new Image();
        img.onload = () => setDimensions({ w: img.naturalWidth, h: img.naturalHeight });
        img.onerror = () => setDimensions(null);
        img.src = absoluteUrl(settings.coverImage);
    }, [settings.coverImage]);

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    if (!open) return null;

    const pickCover = () => fileRef.current?.click();

    const handleFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;

        if (!bookId) {
            onUploadError?.('احفظ الكتاب أولًا قبل رفع الغلاف');
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            onUploadError?.('الصورة كبيرة جدًا (الحد ٥ ميجابايت)');
            return;
        }

        // Read dimensions before uploading so the user sees them immediately.
        const localUrl = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            setDimensions({ w: img.naturalWidth, h: img.naturalHeight });
            URL.revokeObjectURL(localUrl);
        };
        img.src = localUrl;

        setUploading(true);
        try {
            const fd = new FormData();
            fd.append('id', bookId);
            fd.append('cover', file);
            const r = await fetch(`${API_BASE_URL}/api/books/cover/`, {
                method: 'POST',
                headers: authHeaders(authTokens),
                body: fd,
            });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            const data = await r.json();
            onCoverChange?.(data.cover_image);
        } catch (err) {
            onUploadError?.('تعذر رفع الغلاف');
        } finally {
            setUploading(false);
        }
    };

    const removeCover = async () => {
        if (!bookId || !settings.coverImage) return;
        setUploading(true);
        try {
            const r = await fetch(`${API_BASE_URL}/api/books/cover/?id=${bookId}`, {
                method: 'DELETE',
                headers: authHeaders(authTokens),
            });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            onCoverChange?.(null);
        } catch (err) {
            onUploadError?.('تعذر حذف الغلاف');
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="bs-overlay" onClick={onClose} role="dialog" aria-modal="true" aria-label="إعدادات الكتاب">
            <div className="bs-panel" onClick={(e) => e.stopPropagation()}>
                <header className="bs-header">
                    <h2>إعدادات الكتاب</h2>
                    <button className="bs-close" onClick={onClose} aria-label="إغلاق">×</button>
                </header>

                <div className="bs-body">
                    <div className="bs-field">
                        <label>الغلاف</label>
                        <div className="bs-cover-row">
                            <div className="bs-cover-preview">
                                {settings.coverImage ? (
                                    <img src={absoluteUrl(settings.coverImage)} alt="غلاف الكتاب" />
                                ) : (
                                    <span>لا يوجد غلاف</span>
                                )}
                            </div>
                            <div className="bs-cover-actions">
                                <button type="button" onClick={pickCover} disabled={uploading}>
                                    {uploading ? 'جارٍ الرفع…' : (settings.coverImage ? 'استبدال الصورة' : 'رفع صورة')}
                                </button>
                                {settings.coverImage && (
                                    <button type="button" className="bs-danger" onClick={removeCover} disabled={uploading}>
                                        حذف الغلاف
                                    </button>
                                )}
                                <input
                                    ref={fileRef}
                                    type="file"
                                    accept="image/png,image/jpeg,image/webp"
                                    hidden
                                    onChange={handleFile}
                                />
                                {dimensions && (
                                    <small className="bs-hint">
                                        الأبعاد: {dimensions.w} × {dimensions.h}px
                                        {' · '}
                                        النسبة {(dimensions.w / dimensions.h).toFixed(2)}
                                        {Math.abs(dimensions.w / dimensions.h - 2 / 3) > 0.1 && (
                                            <span className="bs-hint--warn"> (يفضّل نسبة 2:3)</span>
                                        )}
                                    </small>
                                )}
                                <small className="bs-hint">JPG / PNG / WebP، الحد ٥ ميجابايت، النسبة المثلى 2:3 (مثلاً 800×1200)</small>
                                {!bookId && (
                                    <small className="bs-hint bs-hint--warn">
                                        احفظ الكتاب أولًا لتفعيل رفع الغلاف
                                    </small>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="bs-field">
                        <label htmlFor="bs-description">نبذة عن الكتاب</label>
                        <textarea
                            id="bs-description"
                            value={settings.description}
                            onChange={(e) => onChange({ description: e.target.value })}
                            placeholder="وصف قصير يظهر للقراء في صفحة الكتاب والمكتبة"
                            maxLength={1000}
                            rows={5}
                        />
                        <small className="bs-hint">{settings.description.length} / 1000</small>
                    </div>

                    <div className="bs-field">
                        <label htmlFor="bs-category">التصنيف</label>
                        <select
                            id="bs-category"
                            value={settings.category}
                            onChange={(e) => onChange({ category: e.target.value })}
                        >
                            {CATEGORIES.map(c => (
                                <option key={c.value} value={c.value}>{c.label}</option>
                            ))}
                        </select>
                    </div>

                    <div className="bs-field bs-field--anon">
                        <label className="bs-checkbox">
                            <input
                                type="checkbox"
                                checked={settings.isAnonymous}
                                onChange={(e) => onChange({ isAnonymous: e.target.checked })}
                            />
                            <span>النشر باسم مجهول</span>
                        </label>
                        <small className="bs-hint">سيظهر اسم المؤلف للقراء كـ "مجهول"</small>

                        {!settings.isAnonymous && (
                            <>
                                <label htmlFor="bs-penname" className="bs-sublabel">اسم مستعار (اختياري)</label>
                                <input
                                    id="bs-penname"
                                    type="text"
                                    value={settings.penName}
                                    onChange={(e) => onChange({ penName: e.target.value })}
                                    placeholder="اسم القلم الذي يظهر للقراء بدلًا من اسم المستخدم"
                                    maxLength={150}
                                />
                            </>
                        )}
                    </div>
                </div>

                <footer className="bs-footer">
                    <button type="button" className="bs-primary" onClick={onClose}>تم</button>
                </footer>
            </div>
        </div>
    );
}
